import React, { useEffect, useState, useCallback } from "react";
import api, { roleLabel, apiError } from "@/lib/api";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Crown, Network, Users, MessageCircle } from "lucide-react";

function flatten(node, level, rows) {
  if (!node || !node.id) return;
  rows.push({ ...node, _level: level });
  (node.children || []).forEach((c) => flatten(c, level + 1, rows));
}

const downline = (n, out = new Set()) => { (n.children || []).forEach((c) => { out.add(c.id); downline(c, out); }); return out; };

const MoveUpline = ({ n, rows, parentId, onDone }) => {
  const blocked = downline(n);
  const opts = rows.filter((r) => r.role !== "owner" && r.id !== n.id && !blocked.has(r.id));
  const move = async (pid) => {
    try { await api.post(`/members/${n.id}/upline`, { parent_id: pid }); toast.success(`${n.name} moved`); onDone(); }
    catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  return (
    <select data-testid={`tree-move-${n.id}`} value={parentId || ""} onChange={(e) => move(e.target.value)} title="Move this member under another member"
      className="mt-2 w-full h-7 rounded-md border border-gold/40 bg-[#05261D] text-[10px] text-white px-1">
      <option value="">Under: Company (direct)</option>
      {opts.map((o) => <option key={o.id} value={o.id}>Under: {o.name}{o.member_no ? ` #${o.member_no}` : ""}</option>)}
    </select>
  );
};

export default function MemberTree() {
  const [rows, setRows] = useState([]);
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    const [t, l] = await Promise.all([api.get("/network/tree"), api.get("/network/levels")]);
    const acc = [];
    flatten(t.data, 0, acc);
    setRows(acc);
    setLevels(l.data);
    setLoading(false);
  }, []);
  useEffect(() => { load(); }, [load]);

  const isCompany = rows[0]?.role === "owner";
  const parentOf = {};
  rows.forEach((r) => (r.children || []).forEach((c) => { parentOf[c.id] = r.role === "owner" ? "" : r.id; }));
  const byLevel = {};
  rows.forEach((r) => { const k = r.role === "owner" ? 0 : (r.level ?? r._level); (byLevel[k] = byLevel[k] || []).push(r); });
  const levelKeys = Object.keys(byLevel).map(Number).sort((a, b) => a - b);
  const levelLabel = (lv) => (lv === 0 ? "ROOT · Company" : `Level ${String(lv).padStart(2, "0")}`);

  return (
    <div>
      <PageHeader eyebrow="Network" title="Member Tree" subtitle="Your downline — who joined under whom, across the 10-level partner network" />
      <div className="p-4 sm:p-8 space-y-8">
        <Card className="p-5 bg-card/40 border-gold/20" data-testid="network-legend">
          <p className="eyebrow text-gold mb-3 flex items-center gap-2"><Network className="w-4 h-4" /> Network Levels</p>
          <div className="flex flex-wrap gap-2">
            {levels.map((l, i) => (
              <Badge key={l.code} variant="outline" className={`${i === 0 ? "border-gold/50 text-gold" : "border-border/60 text-muted-foreground"} font-mono text-[10px]`}>
                {i === 0 ? `${l.code} · ${l.title}` : `Level ${l.code.slice(2)} · Associate Partner`}
              </Badge>
            ))}
          </div>
        </Card>

        <div className="space-y-6" data-testid="network-tree">
          {loading && <p className="text-sm text-muted-foreground text-center py-8">Loading network…</p>}
          {!loading && rows.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">No network members yet.</p>}
          {!loading && levelKeys.map((lv) => (
            <div key={lv} data-testid={`network-level-${lv}`}>
              <div className="flex items-center gap-2 mb-3">
                <Badge variant="outline" className="border-gold/50 text-gold font-mono text-[10px]">{levelLabel(lv)}</Badge>
                <span className="text-[11px] text-muted-foreground">{byLevel[lv].length} member{byLevel[lv].length > 1 ? "s" : ""}</span>
                <div className="flex-1 h-px bg-border/50" />
              </div>
              <div className="flex flex-wrap gap-3">
                {byLevel[lv].map((n) => (
                  <Card key={n.id} data-testid={`tree-node-${n.id}`} className="p-3 bg-card/50 border-gold/20 card-hover w-[150px] flex flex-col items-center text-center">
                    <div className="w-11 h-11 rounded-full border border-gold/40 bg-primary/50 flex items-center justify-center text-base font-serif text-gold">
                      {lv === 0 ? <Crown className="w-5 h-5 text-gold" /> : (n.name || "?")[0]}
                    </div>
                    <p className="font-serif font-semibold text-white text-sm mt-2 leading-tight">{n.name}</p>
                    {n.member_no && <p className="text-[10px] text-gold font-mono" data-testid={`tree-member-no-${n.id}`}>Member #{n.member_no}</p>}
                    <Badge variant="outline" className="mt-1 border-border/60 text-muted-foreground text-[9px] capitalize">{n.level > 0 && n.role !== "owner" ? `Associate Partner · Level ${String(n.level).padStart(2, "0")}` : roleLabel(n.role)}</Badge>
                    <p className="text-[10px] text-muted-foreground mt-1">{n.customers} customers</p>
                    {n.pending && <Badge className="mt-1 bg-amber-500/20 text-amber-200 border border-amber-400/40 text-[9px]" data-testid={`tree-pending-${n.id}`}>Awaiting approval</Badge>}
                    {n.customer_list?.length > 0 && (
                      <details className="mt-1 w-full text-left" data-testid={`tree-customers-${n.id}`}>
                        <summary className="cursor-pointer text-[10px] text-gold">View customers</summary>
                        <ul className="mt-1 space-y-0.5 max-h-28 overflow-y-auto">
                          {n.customer_list.map((c, i) => <li key={i} className="text-[10px] text-white/80 truncate">{c.customer_no ? `#${c.customer_no} ` : ""}{c.name}</li>)}
                        </ul>
                      </details>
                    )}
                    {n.under_owner && <p className="text-[9px] text-emerald-300 mt-0.5" data-testid={`tree-under-${n.id}`}>Under {n.under_owner}</p>}
                    {n.referral_code && <p className="text-[9px] text-gold/70 font-mono mt-0.5">{n.referral_code}</p>}
                    {n.referral_code && (
                      <a data-testid={`tree-wa-${n.id}`} href={`https://wa.me/?text=${encodeURIComponent(`Assalamu Alaikum! Join Bengal Umrah House Travels through ${n.name}: ${window.location.origin}/portal?ref=${n.referral_code}`)}`} target="_blank" rel="noopener noreferrer"
                        className="mt-1.5 inline-flex items-center gap-1 h-6 px-2 rounded-md bg-[#25D366]/15 text-[#25D366] border border-[#25D366]/40 hover:bg-[#25D366]/25 transition-colors text-[9px] font-medium" title="Share joining link on WhatsApp">
                        <MessageCircle className="w-3 h-3" /> WhatsApp
                      </a>
                    )}
                    {isCompany && lv > 0 && <MoveUpline n={n} rows={rows} parentId={parentOf[n.id]} onDone={load} />}
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>

        <Card className="p-4 bg-card/30 border-border/50">
          <p className="text-xs text-muted-foreground flex items-start gap-2"><Users className="w-4 h-4 text-gold mt-0.5 flex-shrink-0" />
            Everyone in the network is an Associate Partner with the same features and commission. Partners who join directly under the company are Level 01; anyone they bring in is Level 02, and so on. The count under each name shows how many customers that partner has brought.{isCompany ? " To fix a member who joined under the wrong person (or as an individual), use the 'Under:' box on their card to move them under the right member — their own downline moves with them." : ""}</p>
        </Card>
      </div>
    </div>
  );
}
